import { Box } from "@mantine/core";
import { modals } from "@mantine/modals";
import * as React from "react";
import { useReactToPrint } from "react-to-print";
import {
  repositoryMdm,
  repositoryPayment,
  repositoryPos,
} from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import type { MessageResponse } from "../../../../model/MessageResponse";
import type { SelectListItem } from "../../../../model/SelectListItem";
import OptionPrintHandover from "../../WarehouseOutWard/pages/PrintPage/OptionPrintHandover";
import OptionPrintOutWard from "../../WarehouseOutWard/pages/PrintPage/OptionPrintOutWard";
import PrintHandover from "../../WarehouseOutWard/pages/PrintPage/PrintHandover";
import PrintPrOutWard from "../../WarehouseOutWard/pages/PrintPage/PrintPrOutWard";
import PrintTheTMDT from "../../WarehouseOutWard/pages/PrintPage/PrintTheTMDT";
import PrintWarehouseLKLR from "../../WarehouseOutWard/pages/PrintPage/PrintWarehouseLKLR";
import PrintWarehouseOutWard from "../../WarehouseOutWard/pages/PrintWarehouseOutWard";
import { getSerialParentInfo } from "../api/saleOutwardApi";
import type { SaleSourceType } from "../config/saleKinds";
import { isDvtBranch } from "../afterLock/saleOutwardAfterLock";
import type { PrintOptions, SaleOrderLineView, SaleOutwardHeader, ScannedSerialRow } from "../types";

/**
 * Điều phối in cho trang xuất bán. Dùng lại nguyên các component in cũ trong
 * WarehouseOutWard/pages (không sửa phần in), chỉ gom phần điều khiển về một hook.
 */

/** Linh kiện lắp ráp của serial, cùng dạng SerialItemLKLR mà các trang in đang dùng */
export interface AssemblyPart {
  itemCode: string;
  itemId: number;
  itemName: string;
  parentSerial: string | null;
  serial: string;
  warranty: number;
  warrantyVendor: number;
}

type LocationData = {
  customerAddress?: any;
  customerName?: any;
  telephoneNumber?: any;
};

const PAGE_STYLE = `
  @page{
    size: A4;
    margin: 7mm 0 7mm 0;
  }
  body:{
    margin: 0;
    color: #000;
  }`;

const parseMoneyValue = (value: any): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null) return 0;
  const parsed = Number(String(value).replace(/[^\d-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Số tiền in trên QR: ưu tiên tiền COD, không có thì tổng giá bán * số lượng */
const getTotalAmountForQR = (printData: any) => {
  const transaction = printData?.tblInventoryTransactionModel;
  const cod = parseMoneyValue(transaction?.codAmount);
  if (cod > 0) return cod;
  return (printData?.inventoryTransactionSerialModels || []).reduce((total: number, item: any) => {
    const price = parseMoneyValue(item?.soPrice);
    const quantity = parseMoneyValue(item?.primaryQuantity);
    return total + (price && quantity ? price * quantity : 0);
  }, 0);
};

const formatToday = () => {
  const today = new Date();
  return `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
};

export interface UseSalePrintArgs {
  sourceType: SaleSourceType;
  header: SaleOutwardHeader;
  branchId: number | null | undefined;
  rows: ScannedSerialRow[];
  orderLines: SaleOrderLineView[];
}

export const useSalePrint = ({ sourceType, header, branchId, rows, orderLines }: UseSalePrintArgs) => {
  const componentRef = React.useRef(null);
  const pickingRef = React.useRef(null);
  const assemblyRef = React.useRef(null);

  const [printTarget, setPrintTarget] = React.useState<{ id: string | number; type: string } | null>(null);
  const [dataPrint, setDataPrint] = React.useState<any>(null);
  const [printOptions, setPrintOptions] = React.useState<PrintOptions | undefined>();
  const printOptionsRef = React.useRef<PrintOptions | undefined>();
  const [handoverMode, setHandoverMode] = React.useState(false);
  const [handoverValues, setHandoverValues] = React.useState<any>(null);
  const [locationData, setLocationData] = React.useState<LocationData>({});
  const [dataCommuneMoi, setDataCommuneMoi] = React.useState<SelectListItem[]>([]);
  const [communeReady, setCommuneReady] = React.useState(false);
  const [qrValue, setQrValue] = React.useState<string | null>(null);
  const [qrReady, setQrReady] = React.useState(false);
  const [paymentSummary, setPaymentSummary] = React.useState<any>(null);
  /** Linh kiện lắp ráp in kèm phiếu xuất (tuỳ chọn trong hộp chọn mẫu in) */
  const [outwardAssemblyParts, setOutwardAssemblyParts] = React.useState<AssemblyPart[] | null>(null);
  /** Linh kiện lắp ráp in riêng từ menu chuột phải của một serial */
  const [assemblyParts, setAssemblyParts] = React.useState<AssemblyPart[] | null>(null);
  const [assemblySerial, setAssemblySerial] = React.useState<string | null>(null);
  const currentDate = React.useMemo(formatToday, []);
  const userInfo = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userLogin") || "null");
    } catch {
      return null;
    }
  }, []);

  const handlePrint = useReactToPrint({ contentRef: componentRef, pageStyle: PAGE_STYLE });
  const printPickingList = useReactToPrint({ contentRef: pickingRef, pageStyle: PAGE_STYLE });
  const printAssembly = useReactToPrint({ contentRef: assemblyRef, pageStyle: PAGE_STYLE });

  /** Gọi từ nút in và từ các hộp chọn mẫu in (OptionPrintOutWard, OptionPrintHandover) */
  const onhandlePrint = React.useCallback(
    (id: string | number, type: string | null, options?: PrintOptions) => {
      if (id == null || type == null) return;
      printOptionsRef.current = options ?? printOptionsRef.current;
      if (options) setPrintOptions(options);
      setPrintTarget({ id, type });
    },
    [],
  );

  // Tải dữ liệu in
  React.useEffect(() => {
    if (!printTarget) return;
    let cancelled = false;
    const load = async () => {
      const withService = sourceType === "XUAT_BAN" && printOptionsRef.current?.dichVuDiKem;
      const res = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/Outward/print-sale-order-out-ward?id=${encodeURIComponent(String(printTarget.id))}${
          withService ? "&isserive=true" : ""
        }`,
      );
      if (cancelled) return;
      if (!res?.success || !res.data) {
        setDataPrint(null);
        setPrintTarget(null);
        NotificationExtension.Fails("Không lấy được dữ liệu in phiếu.");
        return;
      }
      const transaction = res.data.tblInventoryTransactionModel;
      if (transaction?.dvtn !== null && transaction?.gv !== "Nhận tại cửa hàng") {
        let delivery: any = {};
        try {
          delivery = JSON.parse(transaction?.attribute3 || "{}");
        } catch {
          delivery = {};
        }
        setLocationData((prev) => ({
          ...prev,
          customerAddress: delivery?.customerAddress,
          customerName: delivery?.customerName,
          telephoneNumber: delivery?.telephoneNumber,
        }));
      }

      // Tóm tắt thanh toán in kèm phiếu xuất bán
      let summary: any = null;
      if (sourceType === "XUAT_BAN" && transaction?.sourceId) {
        try {
          const mdm = await repositoryMdm.get<MessageResponse<any[]>>(
            `/api/v1/TblOrderHeader/print-order?ids=${transaction.sourceId}`,
          );
          const od = mdm?.success ? mdm.data?.[0] : null;
          summary = od
            ? {
                cashAmount: od?.cashAmount,
                transferAmount: od?.custTransferTotal,
                cardAmount: od?.cardAmount,
                totalPayBefore: od?.payAmount,
                codAmount: od?.codAmount,
                payLaterMethod: od?.attribute20,
              }
            : null;
        } catch {
          summary = null;
        }
      }
      if (cancelled) return;
      setPaymentSummary(summary);
      setDataPrint(res.data);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [printTarget, sourceType]);

  // Phường/xã mới cho địa chỉ in
  React.useEffect(() => {
    if (!dataPrint) return;
    const transaction = dataPrint?.tblInventoryTransactionModel;
    let delivery: any = null;
    try {
      delivery = transaction?.attribute3 ? JSON.parse(transaction.attribute3) : null;
    } catch {
      delivery = null;
    }
    const communeId = transaction?.dvtn
      ? delivery?.customerCommuneId
      : transaction?.newCommuneId || transaction?.cusCommuneId;

    setCommuneReady(false);
    if (!communeId) {
      setDataCommuneMoi([]);
      setCommuneReady(true);
      return;
    }
    repositoryMdm
      .get<MessageResponse<SelectListItem[]>>(`/api/v1/TblDmCommuneMoi/get-select?Ids=${communeId}`)
      .then((res) => setDataCommuneMoi(res?.success ? res.data : []))
      .finally(() => setCommuneReady(true));
  }, [dataPrint]);

  // Mã QR thanh toán khi người dùng chọn in QR
  React.useEffect(() => {
    if (!dataPrint) {
      setQrReady(false);
      return;
    }
    if (!printOptionsRef.current?.inQR) {
      setQrValue(null);
      setQrReady(true);
      return;
    }
    setQrReady(false);
    const createQr = async () => {
      if (await isDvtBranch(branchId)) {
        setQrValue("DVT_STATIC_QR");
        return;
      }
      const transaction = dataPrint?.tblInventoryTransactionModel;
      const orderNumber =
        transaction?.sourceCode || transaction?.transactionCode || header.sourceCode || String(printTarget?.id ?? "");
      try {
        const ip = await repositoryPos.get<MessageResponse<any>>("/api/v1/Public/ip");
        const qr = await repositoryPayment.post<MessageResponse<any>>("/api/TblMBQRcode/gen-qr-mb-v2", {
          amount: getTotalAmountForQR(dataPrint),
          ipAddress: ip?.data || "",
          orderInfo: orderNumber,
          orderNumber,
        });
        if (qr?.success && qr?.data?.qr_url) {
          setQrValue(qr.data.qr_url);
        } else {
          setQrValue(null);
          NotificationExtension.Warn("Không tạo được mã QR thanh toán cho phiếu này");
        }
      } catch {
        setQrValue(null);
        NotificationExtension.Warn("Không tạo được mã QR thanh toán cho phiếu này");
      }
    };
    createQr().finally(() => setQrReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPrint]);

  // Đủ dữ liệu thì in, rồi dọn trạng thái
  React.useEffect(() => {
    if (dataPrint && communeReady && qrReady) {
      handlePrint();
      printOptionsRef.current = printOptionsRef.current ? { ...printOptionsRef.current, inQR: false } : undefined;
      setPrintTarget(null);
      setDataPrint(null);
      setQrValue(null);
    }
  }, [dataPrint, communeReady, qrReady, handlePrint]);

  // In linh kiện lắp ráp khi đã tải xong
  React.useEffect(() => {
    if (assemblyParts && assemblyParts.length > 0 && assemblySerial) {
      printAssembly();
      setAssemblyParts(null);
    }
  }, [assemblyParts, assemblySerial, printAssembly]);

  const printAssemblyParts = React.useCallback(async (row: ScannedSerialRow | undefined) => {
    const serial = row?.serial || "";
    setAssemblySerial(serial);
    try {
      const res = await getSerialParentInfo(serial);
      const parts = [...(res?.data ?? [])];
      const parentIndex = parts.findIndex((d: any) => String(d?.serial ?? "").trim() === serial.trim());
      if (parentIndex > 0) parts.unshift(...parts.splice(parentIndex, 1));
      setAssemblyParts(parts);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const isEcommerce = sourceType === "XUAT_BAN" && !!header.ecommerceOrderId;
  const ecommerceButton = React.useMemo(
    () => (isEcommerce ? <PrintTheTMDT autoPrint={false} idPrintCreate={header.ecommerceOrderId} /> : null),
    [isEcommerce, header.ecommerceOrderId],
  );

  /** Xuất bán: mở hộp chọn mẫu in. Đổi trả: in thẳng */
  const openPrintOptions = React.useCallback(
    (id: number) => {
      setHandoverMode(false);
      modals.openConfirmModal({
        title: "Chọn loại phiếu Xuất Kho Bán",
        closeOnConfirm: false,
        size: "400px",
        children: (
          <OptionPrintOutWard
            id={id}
            type={sourceType}
            onhandlePrint={onhandlePrint}
            setValuePrintOptionModal={setPrintOptions}
            isTMDT={ecommerceButton ?? <></>}
            isTmdtOrder={isEcommerce}
            setDataLinhKienLR={setOutwardAssemblyParts}
            seriaLinhKienLR={rows}
          />
        ),
        confirmProps: { display: "none" },
        cancelProps: { display: "none" },
      });
    },
    [sourceType, onhandlePrint, ecommerceButton, isEcommerce, rows],
  );

  const openHandoverOptions = React.useCallback(
    (id: number) => {
      setHandoverMode(true);
      modals.openConfirmModal({
        title: "Nhập thông tin",
        closeOnConfirm: false,
        size: "550px",
        children: (
          <OptionPrintHandover
            id={id}
            type={sourceType}
            onhandlePrint={onhandlePrint}
            setValuePrintHandoverModal={setHandoverValues}
          />
        ),
        style: { height: "auto" },
        confirmProps: { display: "none" },
        cancelProps: { display: "none" },
      });
    },
    [sourceType, onhandlePrint],
  );

  const printOutward = React.useCallback(
    (id: number, withOptions: boolean) => {
      if (withOptions) {
        openPrintOptions(id);
      } else {
        setHandoverMode(false);
        onhandlePrint(id, sourceType);
      }
    },
    [openPrintOptions, onhandlePrint, sourceType],
  );

  const downloadHandoverTemplate = React.useCallback(() => {
    fetch("/Bien-ban-ban-giao-va-nghiem-thu-hang-hoa.docx").then((response) =>
      response.blob().then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Bien-ban-ban-giao-va-nghiem-thu-hang-hoa.docx";
        a.click();
      }),
    );
  }, []);

  // Chỉ mount template in khi đang in, để các lượt quét không phải render lại trang in 2.600 dòng
  const printArea = (
    <Box style={{ display: "none" }}>
      {dataPrint &&
        (handoverMode ? (
          <PrintHandover
            dataPrint={dataPrint}
            componentRef={componentRef}
            valuePrintHandoverModal={handoverValues}
            locationData={locationData}
            dataCommuneMoi={dataCommuneMoi}
          />
        ) : (
          <PrintWarehouseOutWard
            dataPrint={dataPrint}
            typePrint={sourceType}
            userInfo={userInfo}
            currentDate={currentDate}
            componentRef={componentRef}
            locationData={locationData}
            valuePrintOptionModal={printOptions}
            dataCommuneMoi={dataCommuneMoi}
            dataLinhKienLR={outwardAssemblyParts}
            valueQRCode={qrValue}
            paymentAmounts={paymentSummary}
          />
        ))}
      <PrintPrOutWard componentRef2={pickingRef} dataPrint={orderLines} sourceCode={header.sourceCode ?? ""} />
      <PrintWarehouseLKLR
        componentRef3={assemblyRef}
        dataLinhKienLR={assemblyParts}
        serialLKPrint={assemblySerial}
        dataOutWard={{ tblItemTransactionHeaderCommand: header }}
      />
    </Box>
  );

  return {
    printArea,
    printOutward,
    openHandoverOptions,
    downloadHandoverTemplate,
    printPickingList,
    printAssemblyParts,
    ecommerceButton,
  };
};
